import { WallData, WallDataBase } from '@/editor/data/wall/Wall';
import { Point2dUtils } from '../math/Point2dUtils';
import { IPoint2d } from '../math/IPoint2d';
import { LineSeg2d } from '../math/LineSeg2d';
import { Line2d } from '../math/Line2d';
import { WallBaseData } from '../data/wall/wallBaseData';
import { WallShapeCalculate } from '@/editor/utils/wallShape/wallShapeCalculate';
import { BaseWallStore } from './base/baseWallStore';
import { TGlobalStores } from '.';
import { ISimpleUniformShapeWallInfo, IWallShape } from '../types/wallShape';
import { getBaseShape } from '../utils/wallShape';
import { getRelativeWallIdsThatNeedUpdateShape } from './utils';
import { initDrawWallInfo } from '../data/wall/utils';
import { ArcSeg2d } from '../math/arcSeg2d';
import { Circle2d } from '../math/circle2d';
import { IShape2d, IShapeSeg2d } from '../types/shape';
import { IUniformWallData } from '../types';
import { arcThresholdValue } from '../math/const';
import { isArc } from '../utils/arc';

class WallStore extends BaseWallStore {
  private _wallShapeCalculate: WallShapeCalculate; // 墙体形状计算

  constructor(gs: TGlobalStores) {
    super(gs);

    this._wallShapeCalculate = new WallShapeCalculate(this);
  }

  getWallDatas() {
    return this.getList();
  }

  getWallData(wallId: string) {
    return this.get(wallId);
  }

  addWallData(wallData: WallData) {
    if (this.get(wallData.id)) {
      this.add(wallData);
      this.computeThenUpdateWallsShape(new Set([wallData.id]));
      return;
    }

    const { correctedWallData, updateShapeSet } = this.correctRelatedWallDataPoints(wallData);

    this.add(correctedWallData);
    this.computeThenUpdateWallsShape(updateShapeSet);
  }

  // 外部调用只能修改墙体基础数据，不能主动修改墙体轮廓，自动计算
  updateWallBaseData(wallId: string, _baseData: Partial<WallBaseData>) {
    const wd = this.get(wallId);
    if (!wd) return;

    // 缓存旧关联墙体
    const prevConnectWallInfos = this.getConnectedWallInfo(wallId);
    let newBaseData = { ...wd.baseData, ..._baseData };
    const updateShapeSet = getRelativeWallIdsThatNeedUpdateShape(wallId, prevConnectWallInfos);

    if (
      this.shouldCorrectWallDataPoints(_baseData) &&
      this.isWallDataBasePointsChanged(wd, { id: wallId, baseData: newBaseData })
    ) {
      const correctResult = this.correctRelatedWallDataPoints(
        {
          ...wd,
          baseData: newBaseData
        },
        new Set([wallId])
      );

      newBaseData = correctResult.correctedWallData.baseData;
      correctResult.updateShapeSet.forEach((id) => {
        updateShapeSet.add(id);
      });
    }

    // 更新墙体基础数据
    this.updateBaseData(wallId, newBaseData);

    // 更新基础轮廓
    const { startShape, endShape, baseShape } = getBaseShape(newBaseData);
    this.updateWallShape(wallId, {
      startShape,
      endShape,
      drawShape: baseShape
    });

    // 更新所有关联墙体轮廓
    this.computeThenUpdateWallsShape(updateShapeSet);
  }

  updateWallShape(wallId: string, drawInfo: IWallShape) {
    this.updateShape(wallId, {
      ...drawInfo,
      drawShape: [drawInfo.drawShape]
    });
  }

  private computeThenUpdateWallsShape(wallIds: string[] | Set<string>) {
    if (Array.isArray(wallIds)) wallIds = new Set(wallIds);
    if (!wallIds.size) return;

    // 更新绘制信息
    this._wallShapeCalculate.updateWallInfos().setWalls(wallIds).compute().updateShapes();
  }

  getWallDataByEndPoints(start: IPoint2d, end: IPoint2d) {
    for (const wallData of this.getList()) {
      const equal =
        (Point2dUtils.equals(wallData.baseData.start, start) && Point2dUtils.equals(wallData.baseData.end, end)) ||
        (Point2dUtils.equals(wallData.baseData.start, end) && Point2dUtils.equals(wallData.baseData.end, start));

      if (equal) return wallData;
    }

    return null;
  }

  getUniformWallData(wallId: string): IUniformWallData | undefined {
    const wallData = this.get(wallId);
    if (!wallData) return;

    return {
      id: wallData.id,
      start: wallData.baseData.start,
      end: wallData.baseData.end,
      thickness: wallData.baseData.thickness,
      center: wallData.baseData.center,
      clockwise: wallData.baseData.clockwise
    };
  }

  getUniformShapeWallInfos(): ISimpleUniformShapeWallInfo[] {
    return this.getWallDatas().map((item) => {
      const uniformWallData: ISimpleUniformShapeWallInfo = {
        id: item.id,
        start: item.baseData.start,
        end: item.baseData.end,
        center: item.baseData.center,
        clockwise: item.baseData.clockwise,
        thickness: item.baseData.thickness,
        startShape: item.drawWallInfo.startShape,
        endShape: item.drawWallInfo.endShape,
        drawShape: item.drawWallInfo.drawShape[0],
        skipCalculate: false
      };

      if (item.baseData.center) {
        uniformWallData.center = item.baseData.center;
        uniformWallData.clockwise = item.baseData.clockwise || false;
      }

      return uniformWallData;
    });
  }

  removeWallDataById(wallId: string) {
    const wd = this.get(wallId);
    if (!wd) return;

    const prevConnectWallInfos = this.getConnectedWallInfo(wallId);

    this.remove(wallId);

    this.computeThenUpdateWallsShape(getRelativeWallIdsThatNeedUpdateShape(wallId, prevConnectWallInfos));
  }

  /**
   * 获取与主墙体端点连接的墙体、端点在主墙体上的墙体、与主墙体端点相交的墙体
   * @param mainWallId 主墙体id
   * @returns 与主墙体起始点连接的墙体
   */
  getConnectedWallInfo(mainWallId: string) {
    const mainWallData = this.get(mainWallId);
    if (!mainWallData) return;

    return this._wallShapeCalculate.updateWallInfos().getConnectedWallInfo(mainWallId);
  }

  // 获取所有墙体关系
  getConnectedWallInfos() {
    return this._wallShapeCalculate.getConnectedWallInfos();
  }

  /* 获取墙体端点与墙体id的映射 */
  getPointMap() {
    const pointMap = new Map<string, string[]>();

    for (const wallData of this.getWallDatas()) {
      const startKey = `${Point2dUtils.toString(wallData.baseData.start)}`;
      const endKey = `${Point2dUtils.toString(wallData.baseData.end)}`;

      const startWallIds = pointMap.get(startKey);
      if (startWallIds) startWallIds.push(wallData.id);
      else pointMap.set(startKey, [wallData.id]);

      const endWallIds = pointMap.get(endKey);
      if (endWallIds) endWallIds.push(wallData.id);
      else pointMap.set(endKey, [wallData.id]);
    }

    return pointMap;
  }

  getGeometricInfos(): { id: string; seg: IShapeSeg2d; shape: IShape2d }[] {
    return this.getWallDatas().map((wallData) => {
      if (isArc(wallData.baseData)) {
        const arcSeg = new ArcSeg2d({
          start: wallData.baseData.start,
          end: wallData.baseData.end,
          center: wallData.baseData.center,
          clockwise: wallData.baseData.clockwise
        });
        return {
          id: wallData.id,
          seg: arcSeg,
          shape: new Circle2d({ center: arcSeg.center, radius: arcSeg.radius })
        };
      }
      return {
        id: wallData.id,
        seg: new LineSeg2d(wallData.baseData.start, wallData.baseData.end),
        shape: new Line2d({ points: { p1: wallData.baseData.start, p2: wallData.baseData.end } })
      };
    });
  }

  updateAllWallShape() {
    const wallDatas = this.getWallDatas();

    // 先把每堵墙的封口按 baseData 复位，再算斜接。
    //
    // 形状计算(convertToCompleteWallInfo)只在 drawShape 为空时才从 baseData 重建封口，否则沿用现有那份，
    // 于是上一轮斜接调整过的封口会被当成「原始封口」带进这一轮；凡是这一轮链式计算没改到的墙，
    // 就一直留着它。正向路径(updateWallBaseData / correctRelatedWallDataPoints)都是先复位再算，
    // 而撤销/重做只恢复 baseData（drawWallInfo 在 extra 表里、不进 history），走的又是这里，
    // 少了复位就会留下上一步的封口 —— 与被移动墙相接的那堵墙轮廓错位，
    // 连带该处连接点哈希变化，墙面(face)认领不回来、材质回默认。
    wallDatas.forEach(({ id, baseData }) => {
      const { startShape, endShape, baseShape } = getBaseShape(baseData);
      this.updateWallShape(id, { startShape, endShape, drawShape: baseShape });
    });

    this.computeThenUpdateWallsShape(wallDatas.map(({ id }) => id));
  }

  clearAll() {
    this.clear();
  }

  coverDatas(wallDataBases: WallDataBase[]) {
    this.clearAll();

    const correctWallDataBases = this.correctWallDataPoints(wallDataBases);
    for (const wb of correctWallDataBases) {
      const wallData = {
        ...wb,
        drawWallInfo: initDrawWallInfo(wb.baseData)
      };
      this.add(wallData);
    }
    this.updateAllWallShape();
  }

  private correctRelatedWallDataPoints(wallData: WallData, excludeWallIds: Set<string> = new Set()) {
    const updateShapeSet = new Set<string>([wallData.id]);

    const relatedWallDataBases = this.getRelatedWallDataBasesForPointCorrection(
      wallData,
      arcThresholdValue,
      excludeWallIds
    );
    if (!relatedWallDataBases.length) return { correctedWallData: wallData, updateShapeSet };

    const correctWallDataBases = this.correctWallDataPoints(
      [...relatedWallDataBases, { id: wallData.id, baseData: wallData.baseData }],
      arcThresholdValue,
      true
    );
    const correctWallDataBaseMap = new Map(correctWallDataBases.map((wb) => [wb.id, wb]));

    for (const relatedWallDataBase of relatedWallDataBases) {
      const correctWallDataBase = correctWallDataBaseMap.get(relatedWallDataBase.id);
      if (!correctWallDataBase) continue;

      const prevConnectWallInfos = this.getConnectedWallInfo(relatedWallDataBase.id);
      this.updateBaseData(relatedWallDataBase.id, {
        start: correctWallDataBase.baseData.start,
        end: correctWallDataBase.baseData.end
      });

      const { startShape, endShape, baseShape } = getBaseShape(correctWallDataBase.baseData);
      this.updateWallShape(relatedWallDataBase.id, {
        startShape,
        endShape,
        drawShape: baseShape
      });

      getRelativeWallIdsThatNeedUpdateShape(relatedWallDataBase.id, prevConnectWallInfos).forEach((id) => {
        updateShapeSet.add(id);
      });
    }

    const correctWallDataBase = correctWallDataBaseMap.get(wallData.id);
    if (!correctWallDataBase) {
      return { correctedWallData: wallData, updateShapeSet };
    }

    return {
      correctedWallData: {
        ...wallData,
        baseData: correctWallDataBase.baseData,
        drawWallInfo: initDrawWallInfo(correctWallDataBase.baseData)
      },
      updateShapeSet
    };
  }

  private getRelatedWallDataBasesForPointCorrection(
    wallData: WallData,
    tolerance: number = arcThresholdValue,
    excludeWallIds: Set<string> = new Set()
  ) {
    const points = [wallData.baseData.start, wallData.baseData.end];

    return this.getWallDatas()
      .filter((wd) => {
        if (excludeWallIds.has(wd.id)) return false;

        return [wd.baseData.start, wd.baseData.end].some((point) =>
          points.some((targetPoint) => Point2dUtils.distanceTo(point, targetPoint) < tolerance)
        );
      })
      .map((wd) => ({ id: wd.id, baseData: wd.baseData }));
  }

  private shouldCorrectWallDataPoints(baseData: Partial<WallBaseData>) {
    return (
      Object.prototype.hasOwnProperty.call(baseData, 'start') || Object.prototype.hasOwnProperty.call(baseData, 'end')
    );
  }

  private isWallDataBasePointsChanged(prev: WallDataBase, next: WallDataBase) {
    return (
      !Point2dUtils.equals(prev.baseData.start, next.baseData.start) ||
      !Point2dUtils.equals(prev.baseData.end, next.baseData.end)
    );
  }

  private correctWallDataPoints(
    wallDataBases: WallDataBase[],
    tolerance: number = arcThresholdValue,
    includeLineWall: boolean = false
  ): WallDataBase[] {
    // 收集所有端点及其所属墙体信息
    const endpoints: Array<{
      point: IPoint2d;
      wallId: string;
      isStart: boolean;
    }> = [];

    wallDataBases.forEach((wallDataBase) => {
      if (includeLineWall || wallDataBase.baseData.center) {
        endpoints.push({ point: wallDataBase.baseData.start, wallId: wallDataBase.id, isStart: true });
        endpoints.push({ point: wallDataBase.baseData.end, wallId: wallDataBase.id, isStart: false });
      }
    });

    // 使用空间索引加速查找
    const corrections = new Map<string, IPoint2d>();
    for (let i = 0; i < endpoints.length; i++) {
      for (let j = i + 1; j < endpoints.length; j++) {
        const dist = Point2dUtils.distanceTo(endpoints[i].point, endpoints[j].point);

        if (dist < tolerance) {
          // 计算平均位置作为合并点
          const mergedPoint = {
            x: (endpoints[i].point.x + endpoints[j].point.x) / 2,
            y: (endpoints[i].point.y + endpoints[j].point.y) / 2
          };

          // 存储修正
          const key1 = `${endpoints[i].wallId}_${endpoints[i].isStart ? 'start' : 'end'}`;
          const key2 = `${endpoints[j].wallId}_${endpoints[j].isStart ? 'start' : 'end'}`;
          corrections.set(key1, mergedPoint);
          corrections.set(key2, mergedPoint);
        }
      }
    }

    // 应用修正
    return wallDataBases.map((wallDataBase) => {
      const startKey = `${wallDataBase.id}_start`;
      const endKey = `${wallDataBase.id}_end`;

      return {
        ...wallDataBase,
        baseData: {
          ...wallDataBase.baseData,
          start: corrections.get(startKey) || wallDataBase.baseData.start,
          end: corrections.get(endKey) || wallDataBase.baseData.end
        }
      };
    });
  }
}

export default WallStore;
